// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * How use it:
 * 1. by dispatch:
 *      dispatch({ type: "xHeader/update", data: { key: "isStick", value: true } });
 *      data => it has "key" -> object key and value
 * 2. by handler:
 *      xHeaderUpdate({ key: "isStick", value: true });
 */

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type xHeaderStateType = Readonly<{
    notificationBar?: boolean;
    searchInputFocus: boolean;
    showDropShadow: boolean;
    linkUrl?: string;
    closeAutoComplete?: boolean;
    signinErrorMessage?: string;
    isChangePasswordModalOpen?: boolean;
    checkboxOption: string;
}>;

export const initialHeaderState: xHeaderStateType = {
    notificationBar: true,
    searchInputFocus: false,
    showDropShadow: false,
    linkUrl: "",
    closeAutoComplete: false,
    signinErrorMessage: "",
    isChangePasswordModalOpen: false,
    checkboxOption: "",
};

const reducer = {
    "xHeader/notificationBar": (draft: Draft<xHeaderStateType>, action: { notificationBar: boolean }) => {
        draft.notificationBar = action.notificationBar;
    },
    "xHeader/searchInputFocus": (draft: Draft<xHeaderStateType>, action: { focus: boolean }) => {
        draft.searchInputFocus = action.focus;
    },
    "xHeader/ShowDropShadow": (
        draft: Draft<xHeaderStateType>,
        action: { showDropShadow: boolean; linkUrl?: string },
    ) => {
        draft.showDropShadow = action.showDropShadow;
        draft.linkUrl = action.linkUrl;
    },
    "xHeader/closeAutoComplete": (draft: Draft<xHeaderStateType>, action: { closeAutoComplete: boolean }) => {
        draft.closeAutoComplete = action.closeAutoComplete;
    },
    "xHeader/signinErrorMessage": (draft: Draft<xHeaderStateType>, action: { signinErrorMessage: string }) => {
        draft.signinErrorMessage = action.signinErrorMessage;
    },
    "xHeader/isChangePasswordModalOpen": (
        draft: Draft<xHeaderStateType>,
        action: { isChangePasswordModalOpen: boolean },
    ) => {
        draft.isChangePasswordModalOpen = action.isChangePasswordModalOpen;
    },
    "xHeader/searchCheckboxOption": (draft: Draft<xHeaderStateType>, action: { checkboxOption: string }) => {
        draft.checkboxOption = action.checkboxOption;
    },
};

const xHeader = createTypedReducerWithImmer(initialHeaderState, reducer);

(reducers as any).xHeader = xHeader;

export type xHeaderState = {
    xHeader: xHeaderStateType;
} & ApplicationState;

type xHeaderReducers = {
    [key: string]: typeof xHeader;
};

export type xHeaderActions = Parameters<xHeaderReducers[keyof xHeaderReducers]>[1] | AnyAction;
